import epsClient from 'enrollment-platform/api/eps.client'
import { getPlanManagementTasks } from 'enrollment-platform/pages/PlanManagementV2/api/plan-management-tasks-api'
import type { PlanManagementTaskDto } from 'enrollment-platform/pages/PlanManagementV2/api/dto/plan-management-task.dto'
import { PlanManagementTaskStatusEnum } from 'enrollment-platform/pages/PlanManagementV2/api/enums/plan-management-task-status.enum'
import type { SurecoCsvDataType } from '../constants'
import { toSortBy, type CsvCompareFieldResult } from './compare-data-api'
import type { StartCompareRunResponse } from './compare-run-api'

const COMPARE_DATA_BASE_PATH = '/api/v1/plan-management-v2/compare-data'

// Enough to cover every successful fetch — there are only a handful per year.
const VERSION_LIST_LIMIT = 500

/** A successful (non-deleted) Ideon fetch that can be compared. */
export type IdeonVersion = {
  id: string
  planYear: number
  fetchedAt: string
  fetchedBy: string
}

export type IdeonCompareRowStatus = 'ADDED' | 'REMOVED' | 'MODIFIED'

export type IdeonCompareStateCounts = {
  added: number
  removed: number
  modified: number
  unchanged: number
  parseErrors: number
}

/** Rows of one data type + state that failed validation and were left out. */
export type IdeonCompareSkippedRows = {
  dataType: string
  state: string
  /** Rows skipped in the base fetch's file. */
  base: number
  /** Rows skipped in the compare-to fetch's file. */
  target: number
  /** The real count is higher (the parser stops recording errors). */
  capped: boolean
  /** CSV field keys that failed, e.g. serviceAreaId. */
  fields: string[]
}

/** plan_compare_runs.summary for a COMPLETED Ideon run. */
export type IdeonCompareSummary = {
  states: string[]
  /** dataType -> state -> counts */
  counts: Partial<
    Record<SurecoCsvDataType, Record<string, IdeonCompareStateCounts>>
  >
  /** Rows skipped because they failed validation (incl. repeated keys). */
  warnings: {
    /** Both fetches combined, all data types — a floor when any is capped. */
    parseErrors: number
    /** Per data type + state. */
    skipped: IdeonCompareSkippedRows[]
  }
}

/** A completed run joined with the two fetches it compared. */
export type IdeonCompareResult = {
  runId: string
  planYear: number
  baseVersion: IdeonVersion
  targetVersion: IdeonVersion
  completedAt: string
  summary: IdeonCompareSummary
}

export type IdeonCompareCounts = {
  addedCount: number
  removedCount: number
  modifiedCount: number
  unchangedCount: number
  total: number
}

export type IdeonCompareFieldResult = {
  baseValue: unknown
  targetValue: unknown
  isModified: boolean
}

export type IdeonCompareRow = {
  id: string
  compareStatus: IdeonCompareRowStatus
  state: string | null
  fieldCompare: Record<string, IdeonCompareFieldResult>
}

export type StartIdeonCompareParams = {
  baseVersionId: string
  targetVersionId: string
}

/**
 * Successful fetches for the version dropdowns. `fetchedAt` is the fetch-log
 * timestamp (`createdAt`). `completedAt` is when the import finished, which
 * runs several minutes later and must not be shown as the fetch time.
 */
export function toIdeonVersions(rows: PlanManagementTaskDto[]): IdeonVersion[] {
  return rows.flatMap((task) =>
    task.planYear === undefined
      ? []
      : [
          {
            id: task.id,
            planYear: task.planYear,
            fetchedAt: task.createdAt,
            // Scheduled fetches have no user — same label as the Ideon Data tab.
            fetchedBy: task.fetchedBy ?? 'System',
          },
        ]
  )
}

export async function listIdeonVersions(): Promise<IdeonVersion[]> {
  const { rows } = await getPlanManagementTasks({
    page: 1,
    limit: VERSION_LIST_LIMIT,
    sort: 'createdAt',
    order: 'DESC',
    status: PlanManagementTaskStatusEnum.SUCCESS,
  })
  return toIdeonVersions(rows)
}

/**
 * Returns COMPLETED straight away when this pair was compared before (the
 * backend reuses the saved result); otherwise the run is RUNNING — poll it
 * with the shared compare-run endpoints.
 */
export async function startIdeonCompare(
  params: StartIdeonCompareParams
): Promise<StartCompareRunResponse> {
  const { data } = await epsClient.post<StartCompareRunResponse>(
    `${COMPARE_DATA_BASE_PATH}/ideon`,
    {
      baseTaskId: params.baseVersionId,
      targetTaskId: params.targetVersionId,
    }
  )
  return data
}

type CompareDataRowDto = {
  id: string
  compareStatus: string
  state: string | null
  fieldCompare: Record<string, CsvCompareFieldResult>
}

function isIdeonRowStatus(value: string): value is IdeonCompareRowStatus {
  return value === 'ADDED' || value === 'REMOVED' || value === 'MODIFIED'
}

// The shared compare-data endpoint reports fields as { csvValue, liveValue }
// from the stored row's point of view: csvValue is the row shown (compare-to
// for ADDED/MODIFIED, base for REMOVED), liveValue is the base side of a
// MODIFIED row.
function toIdeonRow(row: CompareDataRowDto): IdeonCompareRow | null {
  if (!isIdeonRowStatus(row.compareStatus)) return null
  const status = row.compareStatus
  const fieldCompare = Object.fromEntries(
    Object.entries(row.fieldCompare).map(([key, field]) => {
      if (status === 'REMOVED') {
        return [
          key,
          { baseValue: field.csvValue, targetValue: null, isModified: false },
        ]
      }
      if (status === 'ADDED') {
        return [
          key,
          { baseValue: null, targetValue: field.csvValue, isModified: false },
        ]
      }
      return [
        key,
        {
          baseValue: field.liveValue,
          targetValue: field.csvValue,
          isModified: field.isModified,
        },
      ]
    })
  )
  return { id: row.id, compareStatus: status, state: row.state, fieldCompare }
}

export async function getIdeonCompareRows(params: {
  runId: string
  dataType: SurecoCsvDataType
  /** Omit for all differences. */
  status?: IdeonCompareRowStatus
  /** Empty = all states. */
  states: string[]
  /** Prefix of a HIOS Plan ID or issuer ID. */
  hiosSearch?: string
  page: number
  limit: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}): Promise<{ items: IdeonCompareRow[]; total: number }> {
  const sortBy = toSortBy(params.sortBy)
  const { data } = await epsClient.get<{
    items: CompareDataRowDto[]
    total: number
  }>(COMPARE_DATA_BASE_PATH, {
    params: {
      runId: params.runId,
      dataType: params.dataType,
      page: params.page,
      limit: params.limit,
      ...(params.status ? { compareStatus: params.status } : {}),
      ...(params.states.length > 0 ? { states: params.states.join(',') } : {}),
      ...(params.hiosSearch ? { hiosSearch: params.hiosSearch } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
    },
  })
  return {
    items: data.items.flatMap((row) => {
      const mapped = toIdeonRow(row)
      return mapped ? [mapped] : []
    }),
    total: data.total,
  }
}
